import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { DetailPageContext, FieldOverrideOptions } from '#layers/block-engine';
import type { AnyItem, CompositePrimaryKey, GlobalCollectionId, GlobalFieldId } from '#layers/data-model';
import type { ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import { computed, toValue } from 'vue';
import { isNullishOrEmpty } from '#layers/base/utils/iterable';
import { CREATE_MODE_ITEM_ID } from '#layers/data-model';
import { isFieldHidden } from '#layers/data-model/utils/fields/fields';
import { parseItemPrimaryKeyFromRouteString } from '#layers/data-model/utils/primary-key';
import { getCollectionFields } from '#layers/data-model/utils/schema/collection';
import { usePermissionsStore } from '~/stores/permissions';
import { sortFieldsBySortKey } from '~/utils/fields/sort-fields';
import { createFormDefaultValues } from '~/utils/form-defaults';

export interface UseFormBlockComputedStateParams {
	collectionId: MaybeRefOrGetter<GlobalCollectionId | undefined>;
	itemId: MaybeRefOrGetter<string | CompositePrimaryKey | undefined>;
	fields: MaybeRefOrGetter<GlobalFieldId[] | undefined>;
	detailPageContext: Pick<DetailPageContext, 'collectionId' | 'itemId' | 'fieldOverrides'> & { isDetailPage: boolean };
	getCollection: (id: GlobalCollectionId) => ResolvedMonospaceCollection | undefined;
}

export interface UseFormBlockComputedStateReturn {
	finalCollectionId: ComputedRef<GlobalCollectionId | undefined>;
	isDetailPageForm: ComputedRef<boolean>;
	finalItemId: ComputedRef<string | CompositePrimaryKey | undefined>;
	parsedFinalItemId: ComputedRef<CompositePrimaryKey | null>;
	collectionData: ComputedRef<ResolvedMonospaceCollection | undefined>;
	computedFields: ComputedRef<Array<ResolvedMonospacePrimitiveField | ResolvedMonospaceSingleRelationField>>;
	getDefaultValues: () => AnyItem;
	getFieldOverride: (fieldId: GlobalFieldId, override: keyof FieldOverrideOptions) => boolean;
}

/**
 * Extracts computed state logic for form blocks.
 * Resolves collection, item, and field configuration from props and detail page context.
 */
export function useFormBlockComputedState(params: UseFormBlockComputedStateParams): UseFormBlockComputedStateReturn {
	const {
		collectionId,
		itemId,
		fields,
		detailPageContext,
		getCollection,
	} = params;

	const permissionsStore = usePermissionsStore();

	/**
	 * Resolves collection ID from props or detail page context.
	 */
	const finalCollectionId = computed(() => {
		const propCollectionId = toValue(collectionId);
		return propCollectionId || detailPageContext.collectionId || undefined;
	});

	/**
	 * Checks if this form is the main form for a detail page
	 * (i.e., collection ID matches the page's collection ID).
	 */
	const isDetailPageForm = computed(() => {
		return detailPageContext.isDetailPage && detailPageContext.collectionId === finalCollectionId.value;
	});

	/**
	 * Resolves item ID from props or detail page context.
	 * Returns '+' for create mode when on detail page form without explicit item ID.
	 */
	const finalItemId = computed(() => {
		const propItemId = toValue(itemId);
		return propItemId || (isDetailPageForm.value ? detailPageContext.itemId || CREATE_MODE_ITEM_ID : undefined);
	});

	/**
	 * Gets the resolved collection data from the schema store.
	 */
	const collectionData = computed(() => {
		return finalCollectionId.value ? getCollection(finalCollectionId.value) : undefined;
	});

	/**
	 * Parses the final item ID into a CompositePrimaryKey.
	 * Returns null for create mode ('+') or when collection data is unavailable.
	 */
	const parsedFinalItemId = computed((): CompositePrimaryKey | null => {
		const itemIdValue = finalItemId.value;

		if (typeof itemIdValue === 'string') {
			// CREATE_MODE_ITEM_ID indicates create mode - return null
			if (itemIdValue === CREATE_MODE_ITEM_ID) {
				return null;
			}

			if (!collectionData.value) {
				return null;
			}

			return parseItemPrimaryKeyFromRouteString(itemIdValue, collectionData.value);
		}

		// Already a CompositePrimaryKey or undefined
		return itemIdValue ?? null;
	});

	const isCreateMode = computed(() => finalItemId.value === CREATE_MODE_ITEM_ID);

	/**
	 * Filters collection fields by:
	 * 1. `fields` prop (if provided, only include those fields)
	 * 2. Hidden fields (excluded unless explicitly included via `fields` prop)
	 */
	const computedFields = computed(() => {
		if (!collectionData.value) {
			return [];
		}

		const fieldsValue = toValue(fields);

		const filteredFields = getCollectionFields(collectionData.value, {
			includeConstrainedFields: false,
		}).filter((field) => {
			if (finalCollectionId.value) {
				const hasFieldPermission = isCreateMode.value
					? permissionsStore.canCreateField(finalCollectionId.value, field.id)
					: permissionsStore.canReadField(finalCollectionId.value, field.id);

				if (!hasFieldPermission) return false;
			}

			// If explicit fields list is provided, only include those (even hidden ones)
			if (!isNullishOrEmpty(fieldsValue)) {
				return fieldsValue.includes(field.id);
			}

			// Otherwise, exclude hidden fields
			return !isFieldHidden(field);
		});

		return sortFieldsBySortKey(filteredFields);
	});

	/**
	 * Creates default values for the form based on collection's primitive fields.
	 */
	function getDefaultValues(): AnyItem {
		return createFormDefaultValues(collectionData.value?.primitiveFields ?? []);
	}

	/**
	 * Gets a field override value from the detail page context.
	 */
	function getFieldOverride(fieldId: GlobalFieldId, override: keyof FieldOverrideOptions): boolean {
		return detailPageContext.fieldOverrides?.[fieldId]?.[override] ?? false;
	}

	return {
		finalCollectionId,
		isDetailPageForm,
		finalItemId,
		parsedFinalItemId,
		collectionData,
		computedFields,
		getDefaultValues,
		getFieldOverride,
	};
}
